import type { AvailableMint, SwapToken } from "../api/checkout";
import { mintKey } from "../api/checkout";
import { networkDisplayName, originBlockchainLabel } from "./networkLabels";

/**
 * One thing the payer can pay with.
 *
 * Two sources, one presentation: a mint Cheddar custodies directly and a token
 * it does not are the same choice to a payer, so they share a key, a label and
 * a row template. Nothing in this module distinguishes them visually — the
 * discriminant exists only because the two produce different session-create
 * payloads.
 */
export type PaymentOption =
  { kind: "mint"; mint: AvailableMint } | { kind: "origin"; token: SwapToken };

export function paymentOptionKey(option: PaymentOption): string {
  return option.kind === "mint"
    ? `mint:${mintKey(option.mint)}`
    : `origin:${option.token.assetId}`;
}

export function paymentOptionSymbol(option: PaymentOption): string {
  return option.kind === "mint" ? option.mint.token : option.token.symbol;
}

export function paymentOptionChainLabel(option: PaymentOption): string {
  if (option.kind === "mint") {
    const testnet = option.mint.isTestnet ? " (Testnet)" : "";
    return `${networkDisplayName(option.mint.chain)}${testnet}`;
  }
  return originBlockchainLabel(option.token.blockchain);
}

export function paymentOptionLabel(option: PaymentOption): string {
  return `${paymentOptionChainLabel(option)} — ${paymentOptionSymbol(option)}`;
}

/**
 * Logo URL for an option, or `null` to let `TokenIcon` render a chip.
 *
 * Only native mints can have one — `Mint.image` is curated per mint we
 * custody. Origin tokens always take the chip: no image exists for them in our
 * data, and fetching one would mean an external request on a payment page.
 */
export function paymentOptionImage(option: PaymentOption): string | null {
  return option.kind === "mint" ? (option.mint.image ?? null) : null;
}

export interface PaymentOptionGroup {
  chainLabel: string;
  options: PaymentOption[];
}

/**
 * Every option, native first, then the rest — so an eligible checkout still
 * opens on the choice it offered before, and the extra tokens read as more of
 * the same list rather than as a second mechanism.
 */
export function buildPaymentOptions(
  mints: AvailableMint[],
  originTokens: SwapToken[],
): PaymentOption[] {
  return [
    ...mints.map((mint): PaymentOption => ({ kind: "mint", mint })),
    ...originTokens.map((token): PaymentOption => ({ kind: "origin", token })),
  ];
}

/** Case-insensitive match on the symbol or the chain name. */
export function matchesPaymentQuery(
  option: PaymentOption,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    paymentOptionSymbol(option).toLowerCase().includes(q) ||
    paymentOptionChainLabel(option).toLowerCase().includes(q)
  );
}

/** Options bucketed by chain, each bucket keeping its incoming order. */
export function groupPaymentOptions(
  options: PaymentOption[],
): PaymentOptionGroup[] {
  const groups: PaymentOptionGroup[] = [];
  const byLabel = new Map<string, PaymentOptionGroup>();
  for (const option of options) {
    const chainLabel = paymentOptionChainLabel(option);
    let group = byLabel.get(chainLabel);
    if (!group) {
      group = { chainLabel, options: [] };
      byLabel.set(chainLabel, group);
      groups.push(group);
    }
    group.options.push(option);
  }
  return groups;
}
